import { VideoPreset } from 'livekit-client';

export type NoiseSuppressionLevel = 'browser' | 'standard' | 'strong';
export type BroadcastQuality = '480p' | '720p' | '1080p' | '1440p';
export type BroadcastFrameRate = 24 | 30 | 60;

export const DEFAULT_NOISE_SUPPRESSION_LEVEL: NoiseSuppressionLevel = 'strong';
export const DEFAULT_BROADCAST_QUALITY: BroadcastQuality = '1080p';
export const DEFAULT_BROADCAST_FRAME_RATE: BroadcastFrameRate = 30;

export const NOISE_SUPPRESSION_OPTIONS: ReadonlyArray<{
  id: NoiseSuppressionLevel;
  label: string;
  description: string;
}> = [
  { id: 'browser', label: '基础', description: '浏览器原生降噪，耗电最低' },
  { id: 'standard', label: '标准', description: 'Krisp 标准模型，兼顾降噪与性能' },
  { id: 'strong', label: '强力', description: 'Krisp 高质量模型，优先压低环境杂音' },
];

export const BROADCAST_QUALITY_OPTIONS: ReadonlyArray<{
  id: BroadcastQuality;
  label: string;
  description: string;
  preset: VideoPreset;
}> = [
  {
    id: '480p',
    label: '480p',
    description: '流畅 · 约 1.5 Mbps（30 FPS）',
    preset: new VideoPreset(854, 480, 1_500_000, 30),
  },
  {
    id: '720p',
    label: '720p',
    description: '高清 · 约 3.2 Mbps（30 FPS）',
    preset: new VideoPreset(1280, 720, 3_200_000, 30),
  },
  {
    id: '1080p',
    label: '1080p',
    description: '全高清 · 约 6 Mbps（30 FPS）',
    preset: new VideoPreset(1920, 1080, 6_000_000, 30),
  },
  {
    id: '1440p',
    label: '1440p',
    description: '超清 · 约 10 Mbps（30 FPS）',
    preset: new VideoPreset(2560, 1440, 10_000_000, 30),
  },
];

export const BROADCAST_FRAME_RATE_OPTIONS: ReadonlyArray<{
  id: BroadcastFrameRate;
  label: string;
  description: string;
}> = [
  { id: 24, label: '24 FPS', description: '省流量，适合弱网或低性能设备' },
  { id: 30, label: '30 FPS', description: '标准流畅度，推荐日常直播使用' },
  { id: 60, label: '60 FPS', description: '高流畅度，需要摄像头与网络支持' },
];

export function getNoiseSuppressionOption(level: NoiseSuppressionLevel) {
  return (
    NOISE_SUPPRESSION_OPTIONS.find((option) => option.id === level) ?? NOISE_SUPPRESSION_OPTIONS[0]
  );
}

export function getBroadcastQualityOption(quality: BroadcastQuality) {
  return (
    BROADCAST_QUALITY_OPTIONS.find((option) => option.id === quality) ??
    BROADCAST_QUALITY_OPTIONS[2]
  );
}

export function getBroadcastFrameRateOption(frameRate: BroadcastFrameRate) {
  return (
    BROADCAST_FRAME_RATE_OPTIONS.find((option) => option.id === frameRate) ??
    BROADCAST_FRAME_RATE_OPTIONS[1]
  );
}

export function getBroadcastVideoEncoding(
  quality: BroadcastQuality,
  frameRate: BroadcastFrameRate,
) {
  const baseEncoding = getBroadcastQualityOption(quality).preset.encoding;
  const bitrateMultiplier = frameRate === 60 ? 1.5 : frameRate === 24 ? 0.85 : 1;
  return {
    ...baseEncoding,
    maxBitrate: Math.round((baseEncoding.maxBitrate ?? 0) * bitrateMultiplier),
    maxFramerate: frameRate,
    priority: 'high' as const,
  };
}

export function getBroadcastSimulcastLayers(frameRate: BroadcastFrameRate) {
  const layerFrameRate = Math.min(frameRate, 30);
  const bitrateMultiplier = frameRate === 24 ? 0.85 : 1;
  return [
    new VideoPreset({
      width: 320,
      height: 180,
      maxBitrate: Math.round(300_000 * bitrateMultiplier),
      maxFramerate: layerFrameRate,
      priority: 'high',
    }),
    new VideoPreset({
      width: 640,
      height: 360,
      maxBitrate: Math.round(900_000 * bitrateMultiplier),
      maxFramerate: layerFrameRate,
    }),
  ];
}

export function getBroadcastSenderEncodingPlan(
  sourceWidth: number | undefined,
  sourceHeight: number | undefined,
  quality: BroadcastQuality,
  frameRate: BroadcastFrameRate,
  layerCount: number,
) {
  const highEncoding = getBroadcastVideoEncoding(quality, frameRate);
  if (layerCount <= 1) {
    return [
      {
        maxBitrate: highEncoding.maxBitrate,
        maxFramerate: frameRate,
        scaleResolutionDownBy: getBroadcastScaleResolutionDownBy(
          sourceWidth,
          sourceHeight,
          quality,
        ),
      },
    ];
  }

  const sourceShortEdge = Math.min(sourceWidth ?? 0, sourceHeight ?? 0);
  const simulcastLayers = getBroadcastSimulcastLayers(frameRate);
  const fallbackPlans = simulcastLayers.map((layer) => ({
    maxBitrate: layer.encoding.maxBitrate,
    maxFramerate: layer.encoding.maxFramerate ?? Math.min(frameRate, 30),
    scaleResolutionDownBy:
      sourceShortEdge > 0 ? Math.max(1, sourceShortEdge / Math.min(layer.width, layer.height)) : 1,
  }));
  const highPlan = {
    maxBitrate: highEncoding.maxBitrate,
    maxFramerate: frameRate,
    scaleResolutionDownBy: getBroadcastScaleResolutionDownBy(sourceWidth, sourceHeight, quality),
  };
  return layerCount === 2 ? [fallbackPlans[1], highPlan] : [...fallbackPlans, highPlan];
}

export function getBroadcastScaleResolutionDownBy(
  sourceWidth: number | undefined,
  sourceHeight: number | undefined,
  quality: BroadcastQuality,
) {
  if (!sourceWidth || !sourceHeight) return 1;
  const target = getBroadcastQualityOption(quality).preset.resolution;
  const sourceLongEdge = Math.max(sourceWidth, sourceHeight);
  const sourceShortEdge = Math.min(sourceWidth, sourceHeight);
  const targetLongEdge = Math.max(target.width, target.height);
  const targetShortEdge = Math.min(target.width, target.height);
  return Math.max(1, Math.min(sourceLongEdge / targetLongEdge, sourceShortEdge / targetShortEdge));
}

export function getMinimumCaptureQuality(quality: BroadcastQuality): BroadcastQuality {
  return quality === '480p' ? '720p' : quality;
}

export function getVideoDegradationPreference(
  _quality: BroadcastQuality,
): RTCDegradationPreference {
  return 'maintain-framerate';
}
